import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import { ticketLabel, langueCodeTicket } from '../utils/serviceCode';
import { orderedServiceLangs } from '../utils/serviceNameLangs';
import { nombresEnMotsAr } from '../utils/nombres-ar';

/**
 * Hook de synthèse vocale TicketFlow — annonces trilingues AR/FR/EN.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Implémentation À FILE UNIQUE : les annonces sont mises en file et jouées
 * SÉQUENTIELLEMENT (chaînage via `onend`), donc les langues s'enchaînent sans
 * `setTimeout` fragile. Chaque annonce résout sa voix / débit / hauteur / volume
 * PAR LANGUE depuis `config.voice.languages[<fr|ar|en>]` (préférence centrale),
 * en repli sur les voix réellement installées sur CE poste
 * (voiceURI exact → 1re voix de la langue → voix par défaut du navigateur).
 *
 * L'orchestration trilingue vit ICI (source unique) via `announceTicketMultilang`
 * / `announceRecallMultilang` : les écrans n'ont plus de logique de langue.
 * L'INSTALLATION des voix OS est hors app (docs/19-CONSIGNES-VOIX-TTS-MULTILINGUE.md).
 */

const LOCALE_OF = { fr: 'fr-FR', ar: 'ar-SA', en: 'en-US' };

/**
 * Ce qui est PRONONCÉ pour un ticket.
 *
 * RÈGLE : la voix dit EXACTEMENT ce que l'écran affiche. Pas ce que l'identifiant contient,
 * pas le code de la langue en cours d'annonce — ce que le client a sous les yeux.
 *
 * C'est le fruit d'un défaut observé. L'écran et la voix résolvaient la langue chacun de son
 * côté : l'écran par l'ordre des langues de `styles.display`, la voix par `voice.order`. Avec
 * un service dont le code FR et le code AR diffèrent, l'écran affichait « RCP20 » pendant que
 * l'annonce disait autre chose — le client cherchait des yeux ce qu'il venait d'entendre.
 *
 * La langue d'AFFICHAGE fait donc foi, y compris quand la phrase est prononcée en arabe : la
 * phrase est traduite, l'identifiant du ticket ne l'est pas. Mieux vaut une phrase arabe
 * contenant « RCP 20 » qu'un client qui ne retrouve pas son ticket au tableau.
 *
 * Depuis que le SERVEUR compose le libellé, cette règle n'est plus à tenir de notre côté : on
 * prononce `ticket.label`, celui-là même qui est affiché et imprimé. Le calcul local reste en
 * repli, le temps que tous les serveurs du parc soient à jour.
 */
function libelleTicket(config, ticket) {
  if (typeof ticket === 'string' || typeof ticket === 'number') return String(ticket);
  if (ticket?.label) return ticket.label;
  const langueAffichee = orderedServiceLangs(config?.styles?.display, 'x')[0]?.key || 'fr';
  return ticketLabel(config, ticket, langueCodeTicket(config, langueAffichee));
}

/** 'fr-FR' → 'fr' ; 'ar' → 'ar'. */
const langKey = (loc) => String(loc || 'fr').slice(0, 2).toLowerCase();

/**
 * Le libellé tel qu'il est PRONONCÉ : la virgule prend la place de l'espace qui sépare le code du
 * numéro final. Une espace ne dit rien à un moteur de synthèse (devant `RCP 13` il hésite entre un
 * nombre et deux, selon la voix installée) ; la virgule ferme le groupe précédent → `13` redevient
 * un entier, et la respiration voulue est obtenue. La ponctuation n'est PAS prononcée : le libellé
 * AFFICHÉ ne bouge pas. Virgule arabe `،` (U+060C) en arabe — la latine y est mal phrasée.
 * (Port V0.2.0 session 2026-09-10.)
 */
function libelleParle(libelle, lang) {
  const virgule = langKey(lang) === 'ar' ? '،' : ',';
  return String(libelle ?? '').replace(/\s+(\d+)\s*$/, `${virgule} $1`);
}

/**
 * Phrase d'appel d'un ticket, par langue.
 *
 * EN ARABE, LES NOMBRES SONT ÉCRITS EN TOUTES LETTRES, et la conversion porte sur la phrase
 * ENTIÈRE — pas sur le seul libellé. Le numéro de guichet est un nombre lui aussi, et il se
 * faisait inverser exactement de la même façon.
 *
 * Mesuré sur le site Yalidine (2026-09-13) : le ticket `تحاليل الرجال 10` était prononcé
 * « ZÉRO UN ». Pas « un zéro » — *zéro un* : le moteur restituait les chiffres dans l'ordre
 * VISUEL du texte arabe, de droite à gauche. Les chiffres arabo-indiens (`١٠`) et la virgule
 * de phrasé ci-dessus subissent le même sort : aucune ÉCRITURE de chiffre n'est normalisée par
 * ce moteur. Le seul texte qu'un moteur n'a pas à normaliser, c'est un MOT.
 *
 * La règle « la voix dit ce que l'écran affiche » tient : un nombre en toutes lettres reste le
 * MÊME nombre. L'écran, le papier et les annonces FR/EN ne changent pas.
 */
function ticketPhrase(lang, ticketId, counterId) {
  const t = libelleParle(ticketId, lang);
  switch (langKey(lang)) {
    case 'ar': return nombresEnMotsAr(`تذكرة رقم ${t}، الشباك رقم ${counterId}`);
    case 'en': return `Ticket ${t}, counter ${counterId}`;
    default: return `Ticket ${t}, guichet ${counterId}`;
  }
}
/** Phrase de rappel, par langue. */
function recallPhrase(lang, ticketId, counterId) {
  const t = libelleParle(ticketId, lang);
  switch (langKey(lang)) {
    case 'ar': return nombresEnMotsAr(`تذكير تذكرة ${t}، الشباك ${counterId}`);
    case 'en': return `Recall ticket ${t}, counter ${counterId}`;
    default: return `Rappel ticket ${t}, guichet ${counterId}`;
  }
}

/**
 * Langues à annoncer, dans l'ordre, d'après `config.voice`. Repli RÉTRO-COMPATIBLE :
 * `config.voice` absent → français (+ arabe si `ui.bilingual`).
 */
function orderedLangs(config) {
  const v = config?.voice;
  if (v && v.enabled !== false && v.languages) {
    const order = Array.isArray(v.order) && v.order.length ? v.order : ['fr', 'ar', 'en'];
    const langs = order.filter((l) => v.languages[l]?.enabled);
    if (langs.length) return langs;
  }
  return config?.ui?.bilingual ? ['fr', 'ar'] : ['fr'];
}

export const useVoiceAnnouncer = (options = {}) => {
  const { config } = useConfig();
  // Test DYNAMIQUE (et non figé au montage) : sur la WebView Android, le polyfill
  // `speechSynthesis` est injecté au document-start mais peut n'être pas encore présent
  // au tout premier rendu React. Figer `supported = false` ici le laisserait faux À JAMAIS
  // (l'effet ci-dessous ne se relancerait pas) → `available` jamais vrai → l'annonce
  // d'appel resterait muette alors que le moteur fonctionne. Le bouton de test, lui, re-teste
  // au clic — d'où le symptôme « le test parle mais pas l'annonce du ticket ».
  const hasSpeech = () => typeof window !== 'undefined' && 'speechSynthesis' in window;

  const [voices, setVoices] = useState([]);
  const [available, setAvailable] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  // Compat descendante (anciens écrans / réglages) — voix « par défaut » manuelle.
  const [selectedVoice, setSelectedVoice] = useState(null);

  const voicesRef = useRef([]);
  const voicesSigRef = useRef(''); // signature de la liste de voix, pour ignorer les événements redondants
  const queueRef = useRef([]);
  const speakingRef = useRef(false);

  // Interrupteur maître : accessibilité + bloc voix (sauf override explicite d'option).
  const enabled = options.enabled !== undefined
    ? options.enabled
    : (config?.accessibility?.audioAnnouncements !== false && config?.voice?.enabled !== false);

  // Chargement des voix OS (synchrone + événement asynchrone).
  useEffect(() => {
    const load = () => {
      if (!hasSpeech()) return;
      const list = window.speechSynthesis.getVoices() || [];
      // IMPORTANT : certains navigateurs (Firefox notamment, surtout sans backend TTS actif)
      // refont `onvoiceschanged` en boucle. Sans garde, chaque appel ferait `setVoices(list)`
      // avec une NOUVELLE référence → re-render permanent → l'effet socket de /display se
      // relançait ~1×/s (churn « Non connecté » qui clignote). On ne met à jour l'état QUE si
      // la liste a réellement changé (comparaison par signature voiceURI/nom).
      const sig = list.map((v) => v.voiceURI || v.name || '').join('|');
      voicesRef.current = list;
      setAvailable(true); // idempotent (même valeur → pas de re-render)
      if (sig !== voicesSigRef.current) {
        voicesSigRef.current = sig;
        setVoices(list);
      }
    };
    // Branche le moteur dès qu'il est là. S'il n'est pas encore présent (course avec
    // l'injection du polyfill Android), on ré-essaie jusqu'à ~15 s au lieu d'abandonner.
    const attach = () => {
      if (!hasSpeech()) return false;
      load();
      window.speechSynthesis.onvoiceschanged = load;
      return true;
    };
    let iv = null;
    if (!attach()) {
      let tries = 0;
      iv = setInterval(() => {
        if (attach() || (tries += 1) > 50) { clearInterval(iv); iv = null; }
      }, 300);
    }
    return () => {
      if (iv) clearInterval(iv);
      if (hasSpeech()) window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  /** Réglages d'une langue depuis la config (voiceURI/locale/rate/pitch/volume). */
  const settingsFor = useCallback((lang) => {
    const k = langKey(lang);
    const c = config?.voice?.languages?.[k] || {};
    return {
      locale: c.locale || LOCALE_OF[k] || lang,
      voiceURI: c.voiceURI || '',
      rate: Number(c.rate) > 0 ? Number(c.rate) : 1,
      pitch: c.pitch != null ? Number(c.pitch) : 1,
      volume: c.volume != null ? Number(c.volume) : 1,
    };
  }, [config]);

  /** Résout la voix effective : voiceURI exact → 1re voix de la langue → défaut navigateur. */
  const resolveVoice = useCallback((voiceURI, locale) => {
    const list = voicesRef.current;
    if (!list.length) return null;
    const k = langKey(locale);
    return (
      (voiceURI && list.find((v) => v.voiceURI === voiceURI)) ||
      list.find((v) => (v.lang || '').toLowerCase().startsWith(k)) ||
      selectedVoice ||
      null
    );
  }, [selectedVoice]);

  const pump = useCallback(() => {
    if (!hasSpeech() || speakingRef.current || queueRef.current.length === 0) return;
    const item = queueRef.current.shift();
    speakingRef.current = true;
    setSpeaking(true);
    const u = new SpeechSynthesisUtterance(item.text);
    u.lang = item.locale;
    const v = resolveVoice(item.voiceURI, item.locale);
    if (v) u.voice = v;
    u.rate = item.rate;
    u.pitch = item.pitch;
    u.volume = item.volume;
    const done = () => {
      speakingRef.current = false;
      setSpeaking(false);
      setTimeout(pump, 250); // court silence entre annonces
    };
    u.onend = done;
    u.onerror = done;
    window.speechSynthesis.speak(u);
  }, [resolveVoice]);

  /** Met un texte en file pour une langue donnée (avec ses réglages config). */
  const announce = useCallback((text, lang) => {
    if (!hasSpeech() || !enabled || !text) return false;
    const s = settingsFor(lang);
    queueRef.current.push({ text, locale: s.locale, voiceURI: s.voiceURI, rate: s.rate, pitch: s.pitch, volume: s.volume });
    pump();
    return true;
  }, [enabled, settingsFor, pump]);

  const announceTicket = useCallback(
    (ticketId, counterId, lang = 'fr-FR') => announce(ticketPhrase(lang, ticketId, counterId), lang),
    [announce],
  );

  const announceWaitingMessage = useCallback((serviceType, count, lang = 'fr-FR') => {
    const k = langKey(lang);
    const text = k === 'ar'
      ? `${count} في قائمة الانتظار للخدمة ${serviceType}`
      : k === 'en'
        ? `${count} customers waiting for service ${serviceType}`
        : `${count} clients en attente pour le service ${serviceType}`;
    return announce(text, lang);
  }, [announce]);

  /** Annonce d'appel TRILINGUE : enchaîne les langues activées (config.voice.order). */
  const announceTicketMultilang = useCallback((ticket, counterId) => {
    if (!enabled) return false;
    orderedLangs(config).forEach((lang) => announce(ticketPhrase(lang, libelleTicket(config, ticket), counterId), LOCALE_OF[lang] || lang));
    return true;
  }, [enabled, config, announce]);

  /** Rappel TRILINGUE : enchaîne les langues activées. */
  const announceRecallMultilang = useCallback((ticket, counterId) => {
    if (!enabled) return false;
    orderedLangs(config).forEach((lang) => announce(recallPhrase(lang, libelleTicket(config, ticket), counterId), LOCALE_OF[lang] || lang));
    return true;
  }, [enabled, config, announce]);

  const clearQueue = useCallback(() => {
    if (hasSpeech()) window.speechSynthesis.cancel();
    queueRef.current = [];
    speakingRef.current = false;
    setSpeaking(false);
  }, []);

  // IMPORTANT : mémoïser l'objet retourné. Sans cela il est recréé à CHAQUE rendu, ce qui
  // fait re-déclencher les useEffect qui l'ont en dépendance (ex. l'effet socket de /display) —
  // provoquant une boucle requestUpdate → setState → re-render, un ré-attachement permanent des
  // écouteurs (mises à jour de tickets perdues) et un écran qui « bouge ». Les callbacks internes
  // sont déjà stables (useCallback) ; seule une vraie évolution d'état/callback change l'objet.
  return useMemo(() => ({
    available,
    speaking,
    voices,
    selectedVoice,
    setSelectedVoice,
    announce,
    announceTicket,
    announceWaitingMessage,
    announceTicketMultilang,
    announceRecallMultilang,
    clearQueue,
  }), [
    available, speaking, voices, selectedVoice, setSelectedVoice,
    announce, announceTicket, announceWaitingMessage,
    announceTicketMultilang, announceRecallMultilang, clearQueue,
  ]);
};

export default useVoiceAnnouncer;
